import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { connect } from 'react-redux';
import {
    getAppDescription,
    getAppImages,
    getAppName,
    getProperties,
} from '../../utils';
import { Grid, Typography, withStyles } from '@material-ui/core';
import styles from './styles';
import Footer from '../../common/Footer';
import AppDownloadLinks from '../../common/AppDownloadLinks';
import Logo from '../../common/Logo';
import { requestShortLinkData } from '../../shortLinks/actions';
import {
    getShortLinkOgTitle,
    getShortLinkOgDescription,
    getShortLinkOgImage,
    isShortLinkLoading,
} from '../../shortLinks/selectors';

const WordpressScreen = ({
    classes,
    fetchShortLinkData,
    ogTitle,
    ogDescription,
    ogImage,
    shortLinkLoading,
}) => {
    const {
        params: { wpPath },
    } = useRouteMatch();
    const { pathname, search } = useLocation();
    const menuItems = [
        { label: 'Terms of Service', route: '/terms-of-service' },
        { label: 'Privacy', route: '/privacy' },
    ];

    // Extract sc query param for shared content
    const queryParams = new URLSearchParams(search);
    const shortCode = queryParams.get('sc');

    useEffect(() => {
        if (shortCode) {
            fetchShortLinkData(shortCode);
        }
    }, [shortCode, fetchShortLinkData]);

    if (!['/download', '/about'].includes(pathname)) {
        let url = `${getProperties()?.wordpressDomain}${pathname}`;

        if (getProperties()?.wordpressOverrides?.[pathname]) {
            url = getProperties()?.wordpressOverrides?.[pathname];
        } else if (wpPath) {
            url = `${getProperties()?.wordpressDomain}/${wpPath}`;
        }
        window.location.assign(url);
    }

    const NavBar = () => {
        return (
            <Grid container className={classes.navBarContainer}>
                <Grid container item className={classes.navBarLogoContainer}>
                    <Grid item className={classes.logoContainer}>
                        <Logo isLogoWhite />
                    </Grid>
                    <Grid
                        container
                        item
                        className={classes.navBarItemContainer}
                    >
                        {menuItems.map((item, i) => (
                            <Grid
                                item
                                href={item.route}
                                key={i}
                                className={classes.navBarItemContainer}
                            >
                                <a
                                    className={classes.navBarItem}
                                    href={item.route}
                                >
                                    +{item.label}
                                </a>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const appName = getAppName();
    const appDescription = getAppDescription();

    // Use og metadata
    const hasSharedContent =
        shortCode && !shortLinkLoading && (ogTitle || ogDescription || ogImage);
    const displayDescription =
        hasSharedContent && ogDescription ? ogDescription : appDescription;

    const SharedContentPreview = () => {
        if (!hasSharedContent || !ogImage) return null;

        return (
            <Grid item className={classes.sharedContentContainer}>
                <img
                    alt={ogTitle || 'Shared content'}
                    src={ogImage}
                    className={classes.sharedContentImage}
                />
            </Grid>
        );
    };

    const HeaderContent = () => (
        <Grid
            container
            className={classes.headerContentContainer}
            key="HeaderContent"
        >
            {/* App store description */}
            <Grid item className={classes.storeDescriptionContainer}>
                <Grid item className={classes.storeDescriptionContainerInner}>
                    <Grid item>
                        {/*    Title */}
                        <Typography className={classes.storeDescriptionTitle}>
                            <>
                                Join your organization on
                                <span
                                    className={classes.storeDescriptionAppName}
                                >
                                    {appName}
                                </span>
                            </>
                        </Typography>
                    </Grid>
                    <Grid item>
                        {/*    Description */}
                        <Typography className={classes.storeDescriptionText}>
                            {displayDescription}
                        </Typography>
                    </Grid>
                    {/* Shared Content Preview */}
                    <SharedContentPreview />
                    {/* App Store Links    */}
                    <Grid item style={{ flex: 1, paddingTop: 30 }}>
                        <AppDownloadLinks
                            classes={{ container: classes.downloadContainer }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {/* Mockup */}
            <Grid item className={classes.storeMockupContainer}>
                <img
                    alt=""
                    src={getAppImages().landingMockup}
                    className={classes.storeMockup}
                />
            </Grid>
        </Grid>
    );

    return (
        <Grid container className={classes.container}>
            <NavBar />
            <HeaderContent />
            <Grid item className={classes.featureContainer}>
                <Grid
                    item
                    className={classes.featureContainerBackgroundContainer}
                >
                    <div className={classes.featureContainerBackground}>
                        Inside the App
                    </div>
                </Grid>
                <Grid item className={classes.appMockupContainer}>
                    <img
                        alt=""
                        src={getAppImages().appMockup}
                        className={classes.appMockup}
                    />
                </Grid>
            </Grid>
            <Footer
                className={classes.footer}
                classes={{
                    footerText: classes.footerText,
                    logo: classes.footerLogo,
                }}
            />
        </Grid>
    );
};

WordpressScreen.propTypes = {
    classes: PropTypes.object.isRequired,
    fetchShortLinkData: PropTypes.func.isRequired,
    ogTitle: PropTypes.string,
    ogDescription: PropTypes.string,
    ogImage: PropTypes.string,
    shortLinkLoading: PropTypes.bool.isRequired,
};

WordpressScreen.defaultProps = {
    ogTitle: null,
    ogDescription: null,
    ogImage: null,
};

const mapStateToProps = (state) => ({
    ogTitle: getShortLinkOgTitle(state),
    ogDescription: getShortLinkOgDescription(state),
    ogImage: getShortLinkOgImage(state),
    shortLinkLoading: isShortLinkLoading(state),
});

const mapDispatchToProps = (dispatch) => ({
    fetchShortLinkData: (shortCode) =>
        dispatch(requestShortLinkData(shortCode)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(WordpressScreen));
